import { localDate, type ImportCsvResponse } from "@teamna/shared";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FileField,
  FormRow,
  FormSection,
  InlineError,
  LockIcon,
  Notice,
  PageHeader,
  Select,
  SkeletonList,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useActiveBusiness, useImportCsv, useOwnerGroups } from "@teamna/web-client/hooks/use-owner.ts";
import { usePackagesAndRules } from "@teamna/web-client/hooks/use-setup.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import {
  csvFields,
  parseCsv,
  previewRows,
  readTable,
  type CsvField,
  type CsvMapping,
  type CsvTable,
  type PreviewRow,
} from "./csv.ts";
import { SaveBar } from "./frame.tsx";

/**
 * Import members from a CSV file (web-app-v2 slice D), under the Import step.
 *
 * Four steps on one page: choose the file, check the column match (found from the header row, and
 * the owner can change it), read the preview with the problems of each row, and import. The file
 * is read in the browser; the API gets rows and writes them in one transaction. Rows with problems
 * are left out of the import and stay in the preview, so the owner can fix the file and import it
 * again: a second import of the same rows changes nothing.
 *
 * This is setup step 5. The groups and the packages usually exist by now, but not always. A group name with no group yet
 * and sessions left with no package yet are not problems: the API keeps them and uses them when
 * the owner saves that group or the first package.
 *
 * Where clients book for themselves (feature O1) it reads a client sheet: one row for each client,
 * with an email, the package, the sessions left and the day the package ends (`CLIENT_CSV_FIELDS`).
 * The same page, the same steps and the same preview; only the columns differ. A client's sessions
 * left need a package that exists, so they do not wait.
 *
 * `sampleFile` is for Storybook only: the page reads it once, as if the owner chose it.
 */
export function SetupImportCsvScreen({ sampleFile }: { sampleFile?: File } = {}) {
  const { business, isLoading, error } = useActiveBusiness();
  const { label } = useLabels();
  const { forChildren } = useShape();
  const businessId = business?.businessId ?? null;

  let body;
  if (isLoading && !business) body = <SkeletonList />;
  else if (error && !business) body = <ErrorState error={error} />;
  else if (!businessId) {
    body = (
      <EmptyState
        icon={<LockIcon size={30} />}
        title="No business to manage"
        message="This account does not run a business yet. Create one from the start page."
      />
    );
  } else body = <CsvBody businessId={businessId} sampleFile={sampleFile} />;

  return (
    <div>
      <PageHeader
        back={{ to: "/owner/setup/import", label: "Back to Import" }}
        title="Import from a CSV file"
        description={
          forChildren
            ? `One row for each ${label("participant")}. The ${label("member")} name and a mobile number are necessary. The other columns are optional.`
            : `One row for each ${label("member")}. The name and a mobile number are necessary. The other columns are optional.`
        }
      />
      {body}
    </div>
  );
}

function CsvBody({ businessId, sampleFile }: { businessId: string; sampleFile?: File }) {
  const { label, labels } = useLabels();
  const { forChildren } = useShape();
  const { business } = useActiveBusiness();
  const fields = csvFields(forChildren);
  const groups = useOwnerGroups(businessId);
  const packages = usePackagesAndRules(businessId);
  const importCsv = useImportCsv(businessId);

  const [fileName, setFileName] = useState<string | null>(null);
  const [table, setTable] = useState<CsvTable | null>(null);
  const [mapping, setMapping] = useState<CsvMapping | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportCsvResponse | null>(null);

  const fieldLabel: Record<CsvField, string> = {
    memberName: `${label("member", { capital: true })} name`,
    phone: "Mobile number",
    participantName: `${label("participant", { capital: true })} name`,
    groupName: label("group", { capital: true }),
    sessionsLeft: "Sessions left",
    paid: "Paid or owed",
    email: "Email",
    packageName: "Package",
    endsOn: "Ends on",
  };

  const groupNames = useMemo(
    () => (groups.data ? groups.data.filter((row) => row.archivedAt === null).map((row) => row.name) : null),
    [groups.data],
  );

  const groupWord = label("group");
  // The packages route answers with the sport template while nothing is saved.
  const hasPackages = packages.data ? !packages.data.fromTemplate : true;
  // The packages a client sheet can name: the saved ones on sale. Null while they load.
  const packageList = useMemo(
    () =>
      packages.data
        ? packages.data.fromTemplate
          ? []
          : packages.data.packages.filter((row) => row.active).map((row) => ({ name: row.name, newClientsOnly: row.newClientsOnly }))
        : null,
    [packages.data],
  );
  const today = localDate(new Date(), business?.timezone);
  const preview = useMemo(
    () =>
      table && mapping
        ? previewRows(table, mapping, { groupNames, groupWord, hasPackages, forSelf: !forChildren, packages: packageList, today })
        : [],
    [table, mapping, groupNames, groupWord, hasPackages, forChildren, packageList, today],
  );
  const ready = preview.filter((row) => row.row !== null);
  const problems = preview.length - ready.length;
  const waiting = ready.filter((row) => row.notes.length > 0).length;
  const columnOptions = useMemo(
    () => (table?.headers ?? []).map((header, index) => ({ id: String(index), header })),
    [table],
  );
  const missingRequired = mapping ? mapping.memberName === null || mapping.phone === null : true;

  async function readFile(file: File) {
    setFileError(null);
    setResult(null);
    setFileName(file.name);
    try {
      const cells = parseCsv(await file.text());
      if (cells.length === 0) {
        setTable(null);
        setMapping(null);
        setFileError("The file is empty. Save the sheet as CSV and choose it again.");
        return;
      }
      const read = readTable(
        cells,
        {
          memberName: [labels.memberSingular],
          participantName: [labels.participantSingular],
          groupName: [labels.groupSingular, labels.groupPlural],
        },
        fields,
      );
      setTable(read.table);
      setMapping(read.mapping);
    } catch {
      setFileError("We could not read that file. Save the sheet as CSV (comma separated) and choose it again.");
    }
  }

  const sampleRead = useRef(false);
  useEffect(() => {
    if (!sampleFile || sampleRead.current) return;
    sampleRead.current = true;
    void readFile(sampleFile);
    // Read the sample one time only, when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleFile]);

  function commit() {
    const rows = ready.map((row) => row.row!).filter(Boolean);
    importCsv.mutate(
      { rows },
      {
        onSuccess: (response) => {
          setResult(response);
          showToast(`${response.rows} ${response.rows === 1 ? "row" : "rows"} imported`);
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3 rounded-card border border-line bg-paper p-5 shadow-card sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-[17px]">1. Choose the file</h2>
          <p className="mt-1 text-[14px] text-ink-2">
            {fileName
              ? `${fileName}: ${table?.rows.length ?? 0} ${(table?.rows.length ?? 0) === 1 ? "row" : "rows"}${table?.hasHeader ? " and a header row" : ""}.`
              : "In your spreadsheet, use File, then Download or Save as, then CSV."}
          </p>
        </div>
        <FileField
          className="shrink-0"
          label="CSV file"
          hideLabel
          showFileName={false}
          accept=".csv,text/csv"
          buttonLabel={fileName ? "Choose another file" : "Choose a .csv file"}
          onFile={(file) => void readFile(file)}
        />
      </section>

      {fileError ? <Notice tone="warning" title={fileError} /> : null}

      {table && mapping ? (
        <FormSection
          title="2. Match the columns"
          description={
            table.hasHeader
              ? "We matched the columns from the header row. Change a match if it is wrong."
              : "The file has no header row. Tell us which column holds what."
          }
        >
          {fields.map((field) => (
            <FormRow
              key={field}
              label={fieldLabel[field]}
              hint={fieldHint(field, { forChildren, label })}
            >
              <Select
                label={`Column for ${fieldLabel[field]}`}
                className="w-full sm:max-w-[320px]"
                value={mapping[field] === null ? "" : String(mapping[field])}
                onChange={(event) =>
                  setMapping({
                    ...mapping,
                    [field]: event.target.value === "" ? null : Number(event.target.value),
                  })
                }
              >
                <option value="">Not in the file</option>
                {columnOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.header}
                  </option>
                ))}
              </Select>
            </FormRow>
          ))}
        </FormSection>
      ) : null}

      {table && mapping ? (
        <section className="flex flex-col gap-3" aria-label="Preview">
          <div>
            <h2 className="text-[19px]">3. Check the rows</h2>
            <p className="mt-1 text-[14px] text-ink-2">
              {missingRequired
                ? `Match the ${fieldLabel.memberName.toLowerCase()} and the mobile number columns first.`
                : `${ready.length} ready. ${problems} with problems${problems > 0 ? ", which are left out. Fix them in the file and import it again." : "."}${waiting > 0 ? ` ${waiting} ${waiting === 1 ? "row waits" : "rows wait"} for a later step. We complete ${waiting === 1 ? "it" : "them"} when you add the ${label("group", { plural: true })} and packages.` : ""}`}
            </p>
          </div>
          <DataTable
            caption="Rows in the file"
            columns={previewColumns(fieldLabel, fields)}
            rows={missingRequired ? [] : preview}
            rowKey={(row) => String(row.line)}
            empty={{
              title: missingRequired ? "Columns not matched" : "No rows",
              message: missingRequired
                ? "Choose a column for the name and for the mobile number."
                : "The file has a header row and nothing under it.",
            }}
            countLabel={(count) => `${count} ${count === 1 ? "row" : "rows"}`}
          />
        </section>
      ) : null}

      {table && mapping && !missingRequired ? (
        <div>
          <h2 className="text-[19px]">4. Import</h2>
          <SaveBar note="Nobody is contacted. A second import of the same rows changes nothing.">
            <Button disabled={ready.length === 0} loading={importCsv.isPending} onClick={commit}>
              Import {ready.length} {ready.length === 1 ? "row" : "rows"}
            </Button>
          </SaveBar>
          {importCsv.error ? (
            <div className="mt-3">
              <InlineError error={importCsv.error} />
            </div>
          ) : null}
        </div>
      ) : null}

      {result ? (
        <Notice tone="success" title="Import done">
          {forChildren
            ? `${result.participantsCreated} ${result.participantsCreated === 1 ? label("participant") : label("participant", { plural: true })} added, ${result.participantsUpdated} moved to a new ${label("group")}, ${result.balancesSet} ${result.balancesSet === 1 ? "balance" : "balances"} set, ${result.unchanged} unchanged. `
            : `${result.participantsCreated} ${result.participantsCreated === 1 ? label("member") : label("member", { plural: true })} added, ${result.balancesSet} ${result.balancesSet === 1 ? "balance" : "balances"} set, ${result.unchanged} unchanged. `}
          {result.groupsWaiting > 0
            ? `${result.groupsWaiting} ${result.groupsWaiting === 1 ? label("participant") : label("participant", { plural: true })} ${result.groupsWaiting === 1 ? "goes" : "go"} into ${result.groupsWaiting === 1 ? `a ${label("group")}` : label("group", { plural: true })} when you add ${result.groupsWaiting === 1 ? "it" : "them"} in Sessions. `
            : ""}
          {result.balancesWaiting > 0
            ? `${result.balancesWaiting} sessions left ${result.balancesWaiting === 1 ? "number is" : "numbers are"} set when you save your first package. `
            : ""}
          <Link to="/owner/parents" className="font-semibold underline">
            Open {label("member", { plural: true })}
          </Link>
        </Notice>
      ) : null}
    </div>
  );
}

/** The line under each column match, in the business's words. */
function fieldHint(
  field: CsvField,
  { forChildren, label }: { forChildren: boolean; label: ReturnType<typeof useLabels>["label"] },
): string {
  switch (field) {
    case "memberName":
    case "phone":
      return "Necessary";
    case "participantName":
      return `Optional. Without it, the ${label("member")} is the ${label("participant")}.`;
    case "paid":
      return 'Optional. "Paid" or "owed". Empty means paid.';
    case "groupName":
      return `Optional. If the ${label("group")} does not exist yet, we put the ${label("participant")} in it when you add it.`;
    case "sessionsLeft":
      return forChildren
        ? "Optional. If you have no package yet, we set the number when you save your first package."
        : "Optional. The sessions left on the package. With no package yet, we keep them as an opening balance, paid.";
    case "email":
      return `Optional. We keep it on the ${label("member")}'s account. It signs in only after a code proves it.`;
    case "packageName":
      return "Optional. The name of a package on your Packages page. Empty means your first package.";
    case "endsOn":
      return "Optional. The last day of the package, for example 31/12/2026 (day first). Empty means the end of this month.";
  }
}

function previewColumns(fieldLabel: Record<CsvField, string>, fields: CsvField[]): DataTableColumn<PreviewRow>[] {
  const dim = (value: string) => (value ? value : <span className="text-ink-3">None</span>);
  const columns: Partial<Record<CsvField, DataTableColumn<PreviewRow>>> = {
    memberName: { id: "member", header: fieldLabel.memberName, cell: (row) => <span className="font-semibold whitespace-nowrap" dir="auto">{dim(row.memberName)}</span> },
    phone: { id: "phone", header: fieldLabel.phone, cell: (row) => <span className="whitespace-nowrap tabular-nums">{dim(row.phone)}</span> },
    participantName: { id: "participant", header: fieldLabel.participantName, hideOnPhone: true, cell: (row) => dim(row.participantName) },
    email: { id: "email", header: fieldLabel.email, hideOnPhone: true, cell: (row) => dim(row.email) },
    groupName: { id: "group", header: fieldLabel.groupName, hideOnPhone: true, cell: (row) => dim(row.groupName) },
    packageName: { id: "package", header: fieldLabel.packageName, hideOnPhone: true, cell: (row) => dim(row.packageName) },
    sessionsLeft: { id: "sessions", header: fieldLabel.sessionsLeft, align: "end", hideOnPhone: true, cell: (row) => dim(row.sessionsLeft) },
    endsOn: { id: "endsOn", header: fieldLabel.endsOn, hideOnPhone: true, cell: (row) => <span className="whitespace-nowrap tabular-nums">{dim(row.endsOn)}</span> },
    paid: { id: "paid", header: fieldLabel.paid, hideOnPhone: true, cell: (row) => dim(row.paid) },
  };
  return [
    { id: "line", header: "Line", width: "64px", cell: (row) => <span className="tabular-nums text-ink-3">{row.line}</span> },
    ...fields.map((field) => columns[field]).filter((column): column is DataTableColumn<PreviewRow> => Boolean(column)),
    {
      id: "check",
      header: "Check",
      cell: (row) =>
        row.errors.length === 0 ? (
          <span className="flex flex-col items-start gap-1">
            <Badge tone="confirmed">Ready</Badge>
            {row.notes.map((message) => (
              <Badge key={message} tone="quiet">
                {message}
              </Badge>
            ))}
          </span>
        ) : (
          <span className="flex flex-col items-start gap-1">
            {row.errors.map((message) => (
              <Badge key={message} tone="cancelled">
                {message}
              </Badge>
            ))}
          </span>
        ),
    },
  ];
}
